import {
  Form,
  FormRow,
  Option,
  PopupHint,
  SegmentedButton,
  Select,
} from '@op/opds.opux';
import { NewDatePicker } from '@op/opds.opux-new-date-picker';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE } from '../../constants/actionTypes';
import catbreeds from '../../constants/cat-breeds';
import { DEFAULT_LOCALE } from '../../constants/common';
import dogbreeds from '../../constants/dog-breeds';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import { FormInput } from '../common/Input/FormInput';

const AnimalQuestions = (insurance) => {
  const { state, validateInsurance } = useInsuranceOffer();
  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });
  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };
  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );
  const animalDetails = state.selectedInsurances[index].questions;
  const dogs = ['Pedigree dog', 'Mixed-breed dog'];
  const cats = ['Pedigree cat', 'Native breed cat'];

  const changeAnimalInsuranceUELN = (animalUELN, key, title, error) => {
    animalDetails[key] = makeInputData(animalUELN, title);
    validateInsurance({
      insurance,
      type: IO_CHANGE_INSURANCE,
      questions: animalDetails,
      questionId: `${insurance.id}-AnimalUELN`,
      error
    });
  };

  const changeAnimalInsuranceName = (animalName, key, title, text, error) => {
    animalDetails[key] = makeInputData(animalName, title, text);
    validateInsurance({
      insurance,
      type: IO_CHANGE_INSURANCE,
      questions: animalDetails,
      questionId: `${insurance.id}-AnimalName`,
      error
    });
  };

  const changeInsuranceDetail = (event, key, title, text, questionId) => {
    animalDetails[key] = makeInputData(event.target.value, title, text,'error.fill.in.missing.information');
    validateInsurance({
      insurance,
      type: IO_CHANGE_INSURANCE,
      questions: animalDetails,
      questionId: buildElementId(insurance.id, questionId),
    });
  };

  const changeInsuranceName = (animalName, error) => {
    changeAnimalInsuranceName(animalName, 'animalName', 'AnimalName', '', error);
  };

  const changeAnimalUELN = (animalUELN, error) => {
    changeAnimalInsuranceUELN(animalUELN, 'animalUELN', 'animalUELN', error);
  };

  const changeInsuranceGender = (event) => {
    changeInsuranceDetail(
      event,
      'gender',
      'AnimalGender',
      `option.gender-${event.target.value.toLowerCase()}`,
      'ANIMAL_GENDER'
    );
  };

  const changeInsuranceBirthDetail = (data, questionId) => {
    const { date, inputValue, invalid } = data;
    if (date !== null) {
      animalDetails.dateOfBirth = {
        date,
        isInvalid: inputValue === '' || invalid,
        ...makeInputData(inputValue, 'time-of-birth'),
      };
      validateInsurance({
        insurance,
        type: IO_CHANGE_INSURANCE,
        questions: animalDetails,
        questionId: buildElementId(insurance.id, questionId),
      });
    }
  };

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

  const getDate = (answer) => {
    return (answer || { date: null }).date;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };

  return (
    <Form spacing="default">
      <div>
        <FormRow
          dataKey={`${insurance.id}-insured-animal`}
          label={<Message id="labelText.insured-animal" />}
          labelFor={buildElementId(insurance.id, 'ANIMAL_TYPE')}
          required
        >
          <Select
            id={buildElementId(insurance.id, 'ANIMAL_TYPE')}
            aria-describedby='animalTypeError'
            value={getValue(animalDetails.animalType)}
            name={`${insurance.id}-animalType`}
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  animalType: makeInputData(
                    e.target.value,
                    'insured-animal',
                    getOptionTitle(e, 'insured-animal'),
                    'error.fill.in.missing.information'
                  ),
                },
                questionId: e.target.id,
              });
            }}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="Pedigree dog" key="Pedigree dog">
              {getMessage('option.insured-animal.001')}
            </option>
            <option value="Mixed-breed dog" key="Mixed-breed dog">
              {getMessage('option.insured-animal.002')}
            </option>
            <option value="Pedigree cat" key="Pedigree cat">
              {getMessage('option.insured-animal.003')}
            </option>
            <option value="Native breed cat" key="Native breed cat">
              {getMessage('option.insured-animal.004')}
            </option>
            <option value="Horse" key="Horse">
              {getMessage('option.insured-animal.005')}
            </option>
          </Select>

          <ValidationError
            id="animalTypeError"
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'ANIMAL_TYPE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>

      {/* Dog questions */}
      {dogs.indexOf(getValue(animalDetails.animalType)) > -1 && (
        <div>
          <FormInput
            type="sport"
            value={getValue(animalDetails.animalName)}
            inputId={buildElementId(insurance.id, 'ANIMAL_NAME')}
            onChange={(animalName , error) => {
              changeInsuranceName(animalName, error);
            }}
            label={getMessage('labelText.AnimalName')}           
            required
          />          

          {getValue(animalDetails.animalType) === 'Pedigree dog' && (
            <FormRow
              dataKey={`${insurance.id}-breed`}
              label={<Message id="labelText.breed" />}
              labelFor={buildElementId(insurance.id, 'ANIMAL_BREED')}
              required
            >
              <Select
                id={buildElementId(insurance.id, 'ANIMAL_BREED')}
                aria-describedby='animalBreedError'
                value={getValue(animalDetails.breed)}
                name={`${insurance.id}-breed`}
                onChange={(event) => {
                  changeInsuranceDetail(
                    event,
                    'breed',
                    'breed',
                    getOptionTitle(event, 'breed-pedigree-dog'),
                    'ANIMAL_BREED'
                  );
                }}
              >
                <option value="choose" key="choose">
                  {getMessage('uiText.choose')}
                </option>
                <OptionMaker dataList={dogbreeds} getMessage={getMessage} />
              </Select>

              <ValidationError
                id="animalBreedError"
                requireVisible={false}
                errorId={buildElementId(insurance.id, 'ANIMAL_BREED')}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
          )}

          <FormRow
            dataKey={`${insurance.id}-time-of-birth`}
            className={'RS-questions-row-date-picker'}
            label={<Message id="labelText.time-of-birth" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_DOG')} 
            required      
          >
            <NewDatePicker
              dateInputProps={
                {
                  inputProps: {
                    id: buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_DOG'),
                  },
                }
              }
              allowPastDates={true}
              value={getDate(animalDetails.dateOfBirth)}
              name={`${insurance.id}-dateOfBirth`}
              onChange={(date) => {
                changeInsuranceBirthDetail(date, 'ANIMAL_DATEOFBIRTH_DOG');
              }}
            />
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_DOG')}
              errorMessageId="errorText.animalDateOfBirthDog"
            />
          </FormRow>
                    
          <FormRow
            dataKey={`${insurance.id}-AnimalGender`}
            label={<Message id="labelText.AnimalGender" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_GENDER')}
          >
            <SegmentedButton
              value={getValue(animalDetails.gender)}
              name={`${insurance.id}-gender`}
              onChange={(event) => {
                changeInsuranceGender(event);
              }}
              id={buildElementId(insurance.id, 'ANIMAL_GENDER')}
            >
              <Option value="Female" key="Female" required>
                {<Message id="option.gender-female" />}
              </Option>
              <Option value="Male" key="Male">
                {<Message id="option.gender-male" />}
              </Option>
            </SegmentedButton>
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_GENDER')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>

          <FormInput
            type="numeric"
            limit={99999}
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            value={getValue(animalDetails.value)}
            inputId={buildElementId(insurance.id, 'ANIMAL_VALUE')}
            onChange={(value , error) => {
              animalDetails.value = makeInputData(
                value,
                'monetary-value-dog'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: animalDetails,
                questionId: `${insurance.id}-monetary-value-dog`,
                error,
              });                
            }}
            label={getMessage('labelText.monetary-value-dog')}     
            hint={
              <PopupHint placement="top">
                <Message id="hintText.monetary-value" />
              </PopupHint>
            }      
            required
          />
        </div>
      )}

      {/* Cat questions */}
      {cats.indexOf(getValue(animalDetails.animalType)) > -1 && (
        <div>
          <FormInput
            type="sport"
            value={getValue(animalDetails.animalName)}
            inputId={buildElementId(insurance.id, 'ANIMAL_NAME')}
            onChange={(animalName , error) => {
              changeInsuranceName(animalName, error);                
            }}
            label={getMessage('labelText.AnimalName')}           
            required
          />         

          {getValue(animalDetails.animalType) === 'Pedigree cat' && (
            <FormRow
              dataKey={`${insurance.id}-breed`}
              label={<Message id="labelText.breed" />}
              labelFor={buildElementId(insurance.id, 'ANIMAL_BREED')}
              required
            >
              <Select
                id={buildElementId(insurance.id, 'ANIMAL_BREED')}
                aria-describedby='animalBreedError'
                value={getValue(animalDetails.breed)}
                name={`${insurance.id}-breed`}
                onChange={(event) => {
                  changeInsuranceDetail(
                    event,
                    'breed',
                    'breed',
                    getOptionTitle(event, 'breed-pedigree-cat'),
                    'ANIMAL_BREED'
                  );
                }}
              >
                <option value="choose" key="choose">
                  {getMessage('uiText.choose')}
                </option>
                <OptionMaker dataList={catbreeds} getMessage={getMessage} />
              </Select>

              <ValidationError
                id="animalBreedError"
                requireVisible={false}
                errorId={buildElementId(insurance.id, 'ANIMAL_BREED')}
                errorMessageId="error.fill.in.missing.information"
              />
            </FormRow>
          )}

          <FormRow
            dataKey={`${insurance.id}-time-of-birth`}
            className={'RS-questions-row-date-picker'}
            label={<Message id="labelText.time-of-birth" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_CAT')}
            required
          >
            <NewDatePicker
              dateInputProps={
                {
                  inputProps: {
                    id: buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_CAT'),
                  },
                }
              }
              allowPastDates={true}
              value={getDate(animalDetails.dateOfBirth)}
              name={`${insurance.id}-dateOfBirth`}
              onChange={(date) => {
                changeInsuranceBirthDetail(date, 'ANIMAL_DATEOFBIRTH_CAT');
              }}
            />
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_CAT')}
              errorMessageId="errorText.animalDateOfBirthCat"
            />
          </FormRow>

          <FormRow
            dataKey={`${insurance.id}-AnimalGender`}
            label={<Message id="labelText.AnimalGender" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_GENDER')}
          >
            <SegmentedButton
              value={getValue(animalDetails.gender)}
              aria-describedby='animalGenderError'
              name={`${insurance.id}-gender`}
              onChange={(event) => {
                changeInsuranceGender(event);
              }}
              id={buildElementId(insurance.id, 'ANIMAL_GENDER')}
            >
              <Option value="Female" key="Female" required>
                {<Message id="option.gender-female" />}
              </Option>
              <Option value="Male" key="Male">
                {<Message id="option.gender-male" />}
              </Option>
            </SegmentedButton>
            <ValidationError
              id="animalGenderError"
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_GENDER')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>

          <FormInput
            type="numeric"
            limit={99999}
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            value={getValue(animalDetails.value)}
            inputId={buildElementId(insurance.id, 'ANIMAL_VALUE')}
            onChange={(value , error) => {
              animalDetails.value = makeInputData(
                value,
                'monetary-value-pedigree-cat'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: animalDetails,
                questionId: `${insurance.id}-monetary-value-pedigree-cat`,
                error,
              });                
            }}
            label={getMessage('labelText.monetary-value-pedigree-cat')}     
            hint={
              <PopupHint placement="top">
                <Message id="hintText.monetary-value" />
              </PopupHint>
            }      
            required
          />         
        </div>
      )}

      {/* Horse questions */}
      {getValue(animalDetails.animalType) === 'Horse' && (
        <div>
           <FormRow
            dataKey={`${insurance.id}-animalPurposeOfHorse`}
            label={<Message id="labelText.animalPurposeOfHorse" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_PURPOSE_OF_HORSE')}
            required
          >
            <Select
              value={getValue(animalDetails.purposeOfHorse)}
              aria-describedby='animalPurposeOfHorseError'
              name={`${insurance.id}-purposeOfHorse`}
              onChange={(event) => {
                changeInsuranceDetail(
                  event,
                  'purposeOfHorse',
                  'animalPurposeOfHorse',
                  getOptionTitle(event, 'animalPurposeOfHorse'),
                  'ANIMAL_PURPOSE_OF_HORSE'
                );
              }}
              id={buildElementId(insurance.id, 'ANIMAL_PURPOSE_OF_HORSE')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="Showjumping or eventing" key="Showjumping or eventing">
                {getMessage('option.animalPurposeOfHorse.001')}
              </option>
              <option value="Recreational use" key="Recreational use">
                {getMessage('option.animalPurposeOfHorse.002')}
              </option>
              <option value="Dressage" key="Dressage">
                {getMessage('option.animalPurposeOfHorse.003')}
              </option>
              <option value="Harness racing" key="Harness racing">
                {getMessage('option.animalPurposeOfHorse.004')}
              </option>
              <option value="Breeding use" key="Breeding use">
                {getMessage('option.animalPurposeOfHorse.005')}
              </option>
              <option value="Other" key="Other">
                {getMessage('option.animalPurposeOfHorse.006')}
              </option>
            </Select>
            <ValidationError
              id="animalPurposeOfHorseError"
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_PURPOSE_OF_HORSE')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>

          <FormInput
            type="alphaNumeric"
            maxLength={20}
            value={getValue(animalDetails.animalUELN)}
            inputId={buildElementId(insurance.id, 'ANIMAL_UELN')}
            onChange={(animalUELN , error) => {
              changeAnimalUELN(animalUELN, error);               
            }}
            label={getMessage('labelText.animalUELN')}           
            required
            hint={
              <PopupHint placement="top">
                <Message id="hintText.animalUELN" />
              </PopupHint>
            }      
          /> 

          <FormInput
            type="sport"
            value={getValue(animalDetails.animalName)}
            inputId={buildElementId(insurance.id, 'ANIMAL_NAME')}
            onChange={(animalName , error) => {
              changeInsuranceName(animalName, error);               
            }}
            label={getMessage('labelText.AnimalName')}           
            required
          />         

          <FormRow
            dataKey={`${insurance.id}-breed-horse`}
            label={<Message id="labelText.breed-horse" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_BREED')}
            required
          >
            <Select
              value={getValue(animalDetails.breed)}
              aria-describedby='animalBreedError'
              name={`${insurance.id}-breed`}
              onChange={(event) => {
                changeInsuranceDetail(
                  event,
                  'breed',
                  'breed-horse',
                  getOptionTitle(event, 'breed-horse'),
                  'ANIMAL_BREED'
                );
              }}
              id={buildElementId(insurance.id, 'ANIMAL_BREED')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option
                value="Thoroughbred or halfbred"
                key="Thoroughbred or halfbred"
              >
                {getMessage('option.breed-horse.001')}
              </option>
              <option value="Coldblood" key="Coldblood">
                {getMessage('option.breed-horse.002')}
              </option>
              <option value="Pony" key="Pony">
                {getMessage('option.breed-horse.003')}
              </option>
            </Select>
            <ValidationError
              id="animalBreedError"
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_BREED')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>

          <FormRow
            dataKey={`${insurance.id}-time-of-birth`}
            className={'RS-questions-row-date-picker'}
            label={<Message id="labelText.time-of-birth" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_HORSE')}
            required
          >
            <NewDatePicker
              dateInputProps={
                {
                  inputProps: {
                    id: buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_HORSE'),
                  },
                }
              }              
              allowPastDates={true}
              value={getDate(animalDetails.dateOfBirth)}
              name={`${insurance.id}-dateOfBirth`}
              onChange={(date) => {
                changeInsuranceBirthDetail(date, 'ANIMAL_DATEOFBIRTH_HORSE');
              }}
            />
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_DATEOFBIRTH_HORSE')}
              errorMessageId="errorText.animalDateOfBirthHorse"
            />
          </FormRow>

          <FormRow
            dataKey={`${insurance.id}-AnimalGender`}
            label={<Message id="labelText.AnimalGender" />}
            labelFor={buildElementId(insurance.id, 'ANIMAL_GENDER')}
            required
          >
            <Select
              value={getValue(animalDetails.gender)}
              aria-describedby='animalGenderError'
              name={`${insurance.id}-gender`}
              onChange={(event) => {
                changeInsuranceDetail(
                  event,
                  'gender',
                  'AnimalGender',
                  getOptionTitle(event, 'gender-horse'),
                  'ANIMAL_GENDER'
                );
              }}
              id={buildElementId(insurance.id, 'ANIMAL_GENDER')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="Stallion" key="Stallion">
                {getMessage('option.gender-horse.001')}
              </option>
              <option value="Gelding" key="Gelding">
                {getMessage('option.gender-horse.002')}
              </option>
              <option value="Mare" key="Mare">
                {getMessage('option.gender-horse.003')}
              </option>
            </Select>
            <ValidationError
              id="animalGenderError"
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'ANIMAL_GENDER')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          <FormInput
            type="numeric"
            limit={99999}
            unit={<Message id="unitText.euros" />}
            unitPos="outside"
            value={getValue(animalDetails.value)}
            inputId={buildElementId(insurance.id, 'ANIMAL_VALUE')}
            onChange={(value , error) => {
              animalDetails.value = makeInputData(
                value,
                'monetary-value-horse'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: animalDetails,
                questionId: `${insurance.id}-monetary-value-horse`,
                error,
              });                
            }}
            label={getMessage('labelText.monetary-value-horse')}     
            hint={
              <PopupHint placement="top">
                <Message id="hintText.monetary-value" />
              </PopupHint>
            }      
            required
          />       
        </div>
      )}
    </Form>
  );
};

const OptionMaker = ({ dataList, getMessage }) =>
  dataList.map(({ value, text }) => (
    <option value={value} key={value}>
      {getMessage(text)}
    </option>
  ));

export default AnimalQuestions;
